import { useState } from "react";
import { useJobLog } from "@/hooks/use-job-log";
import DataTable from "@/components/DataTable";
import TradeToolShell, { type ToolCategory } from "@/components/TradeToolShell";
import { wireHeaders, wireRows, conduitHeaders, conduitRows, ELECTRICAL_CATEGORIES as BASE_CATEGORIES } from "@/components/electrical/electrical-helpers";
import { GroundLookup, MotorFLA, WireSizing, VoltageDropTable } from "@/components/electrical/ConductorTools";
import { ConduitFillCalc, BoxFillCalc, MultiRacewayFill, ConduitBendingTool } from "@/components/electrical/RacewayTools";
import { MotorStarterSizing, DisconnectSizing, MotorFeederSizing, VFDSizingTool } from "@/components/electrical/MotorTools";
import { TransformerSizing, PanelScheduleCalc, FaultCurrentCalc, PowerFactorCorrection, DemandFactorCalc, ServiceEntranceSizing } from "@/components/electrical/EquipmentTools";
import { VoltageDropCalc, LightingLoadCalc, bendRadiusHeaders, bendRadiusRows } from "@/components/electrical/ElectricalExtras";
import TradeFormulas from "@/components/TradeFormulas";

// Formulas live at the end of the equipment category
const ELECTRICAL_CATEGORIES: ToolCategory[] = BASE_CATEGORIES.map(cat =>
  cat.id === "equipment"
    ? { ...cat, tools: [...cat.tools, { value: "formulas", label: "Formulas" }] }
    : cat
);

const Electrical = () => {
  const { addEntry } = useJobLog();
  const [sharedDemandVA, setSharedDemandVA] = useState<number | null>(null);
  const [cameFromTool, setCameFromTool] = useState<string | null>(null);

  const renderTool = (activeTool: string, goTo: (tool: string) => void) => {
    const sendToService = (from: string) => (va: number) => {
      setSharedDemandVA(va);
      setCameFromTool(from);
      goTo("service");
    };

    switch (activeTool) {
      case "wire": return <div className="-mx-4"><DataTable headers={wireHeaders} rows={wireRows} searchable searchPlaceholder="Search wire sizes…" favoritesConfig={{ type: "wire", category: "⚡ Wire Ampacity", makeId: (row) => `wire-${row[0]}`, makeName: (row) => `${row[0]} AWG`, makeDetail: (row) => `Cu ${row[3]}A · Al ${row[4]}A · ${row[2]} kcmil` }} /></div>;
      case "conduit": return <div className="-mx-4"><DataTable headers={conduitHeaders} rows={conduitRows} searchable searchPlaceholder="Search conduit sizes…" favoritesConfig={{ type: "wire", category: "⚡ Conduit", makeId: (row) => `conduit-${row[0]}`, makeName: (row) => `Conduit ${row[0]}`, makeDetail: (row) => `ID ${row[1]}" · Area ${row[2]} in² · 40% Fill ${row[3]} in²` }} /></div>;
      case "ground": return <GroundLookup onSave={addEntry} />;
      case "motor": return <MotorFLA />;
      case "sizing": return <WireSizing onSave={addEntry} />;
      case "fill": return <ConduitFillCalc onSave={addEntry} />;
      case "boxfill": return <BoxFillCalc onSave={addEntry} />;
      case "xfmr": return <TransformerSizing onSave={addEntry} />;
      case "vdtable": return <VoltageDropTable />;
      case "raceway": return <MultiRacewayFill onSave={addEntry} />;
      case "panel": return <PanelScheduleCalc onSendToService={sendToService("panel")} onSave={addEntry} />;
      case "fault": return <FaultCurrentCalc onSave={addEntry} />;
      case "bending": return <ConduitBendingTool onSave={addEntry} />;
      case "starter": return <MotorStarterSizing onSave={addEntry} />;
      case "disco": return <DisconnectSizing onSave={addEntry} />;
      case "feeder": return <MotorFeederSizing onSave={addEntry} />;
      case "vfd": return <VFDSizingTool onSave={addEntry} />;
      case "pfc": return <PowerFactorCorrection onSave={addEntry} />;
      case "demand": return <DemandFactorCalc onSave={addEntry} onSendToService={sendToService("demand")} />;
      case "service": return (
        <ServiceEntranceSizing
          onSave={addEntry}
          initialDemandVA={sharedDemandVA}
          onConsumedInitial={() => setSharedDemandVA(null)}
          cameFrom={cameFromTool}
          onGoBack={(tool) => { setCameFromTool(null); goTo(tool); }}
        />
      );
      case "vdcalc": return <VoltageDropCalc />;
      case "bendradius": return <div className="-mx-4"><DataTable headers={bendRadiusHeaders} rows={bendRadiusRows} searchable searchPlaceholder="Search bend radius…" favoritesConfig={{ type: "wire", category: "⚡ Bend Radius", makeId: (row) => `bendrad-${row[0]}`, makeName: (row) => `Bend ${row[0]}`, makeDetail: (row) => `EMT ${row[1]}" · IMC ${row[2]}" · RMC ${row[3]}"` }} /></div>;
      case "lighting": return <LightingLoadCalc />;
      case "formulas": return <TradeFormulas trade="electrical" />;
      default: return null;
    }
  };

  return (
    <TradeToolShell
      title="⚡ Electrical"
      subtitle="Wire, conduit, grounding, motor, sizing, fill & more"
      categories={ELECTRICAL_CATEGORIES}
      renderTool={renderTool}
    />
  );
};

export default Electrical;
